import React, { useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedSection from '../common/AnimatedSection';

// const max_images = 50;
// const batch_size = 8;

const GalleryPreview = ({ data = [], loadMore, loading, hasMore }) => {
  const observer_target = useRef(null);

  // Trigger load more (delegated to parent or hook)
  const handleLoadMore = useCallback(() => {
    if (loading || !hasMore) return;
    if (loadMore) loadMore();
  }, [loading, hasMore, loadMore]);

  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px',
      }
    );

    if (observer_target.current) {
      observer.observe(observer_target.current);
    }

    return () => observer.disconnect();
  }, [handleLoadMore]);

  return (
    <section id="gallery" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <AnimatedSection>
            <div className="mb-4">
              <span className="inline-block px-4 py-1.5 bg-primary/[0.06] text-primary text-xs font-bold rounded-full tracking-widest uppercase">
                our milestones
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-dark leading-tight tracking-tight max-w-2xl">
              Capturing the <span className="text-primary">impact</span> of professional excellence
            </h2>
          </AnimatedSection>
        </div>

        {/* GRID */}
        <div>
          <AnimatePresence mode="popLayout">
            {data?.map((item, ind) => (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" key={item?.id || ind}>
                {item?.images?.map((img, i) => (
                  <motion.div
                    key={img?.id || i}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                    whileHover={{ y: -8 }}
                    className="group relative aspect-square rounded-3xl overflow-hidden bg-dark/[0.03] border border-dark/[0.04]"
                  >
                    <img
                      src={img?.image}
                      alt={img?.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 p-6 flex flex-col justify-end">
                      <span className="text-primary text-[10px] font-bold uppercase tracking-widest mb-1">
                        {img?.category}
                      </span>
                      <h4 className="text-white font-bold text-sm">
                        {img?.title}
                      </h4>
                    </div>
                  </motion.div>
                ))}
              </div>
            ))}
          </AnimatePresence>
        </div>

        {/* OBSERVER + STATUS */}
        <div
          ref={observer_target}
          className="w-full py-16 flex flex-col items-center justify-center"
        >
          {loading && (
            <div className="flex gap-2">
              {[0, 1, 2].map((dot) => (
                <motion.div
                  key={dot}
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ repeat: Infinity, duration: 1, delay: dot * 0.2 }}
                  className="w-2.5 h-2.5 rounded-full bg-primary"
                />
              ))}
            </div>
          )}

          {!hasMore && data?.length > 0 && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-dark/30 text-[10px] font-black uppercase tracking-[0.3em]"
            >
              showing all gallery milestones
            </motion.p>
          )}
        </div>

      </div>
    </section>
  );
};

export default GalleryPreview;